import type { ItemListSchema, OfferFull, OfferMedium, Schema, SeoData } from '~/types/Catalog';

type OfferWithSeoHeader = Pick<OfferFull | OfferMedium, 'product_name'> &
  Partial<Pick<OfferFull | OfferMedium, 'product_name_with_offer_properties' | 'seo_data'>>;

type CatalogSeoQueryValue = string | (string | null)[] | null | undefined;
type CatalogSeoQuery = Record<string, CatalogSeoQueryValue>;

export interface CatalogSeoIndexing {
  canonicalPath: string;
  robots: string;
}

const SERVICE_QUERY_PARAMETERS = new Set(['_openstat', 'from', 'gclid', 'roistat', 'yclid', 'ymclid']);

function isServiceQueryParameter(name: string): boolean {
  return name.startsWith('utm_') || SERVICE_QUERY_PARAMETERS.has(name);
}

function getSingleQueryValue(value: CatalogSeoQueryValue): string | null {
  if (Array.isArray(value)) return value.length === 1 ? (value[0] ?? null) : null;

  return typeof value === 'string' ? value : null;
}

export function getCatalogSeoIndexing(
  canonicalPath: string,
  query: CatalogSeoQuery,
  indexableRobots = 'index, follow',
): CatalogSeoIndexing {
  const baseCanonicalPath = canonicalPath.replace(/[?#].*$/, '');
  const hasPageParameter = Object.prototype.hasOwnProperty.call(query, 'page');
  const pageValue = getSingleQueryValue(query.page);
  const page = hasPageParameter ? Number(pageValue) : 1;
  const hasValidPage =
    !hasPageParameter || (pageValue !== null && Number.isInteger(page) && page >= 1 && String(page) === pageValue);
  const contentParameters = Object.keys(query).filter((name) => name !== 'page' && !isServiceQueryParameter(name));

  if (!hasValidPage || contentParameters.length > 0) {
    return { canonicalPath: baseCanonicalPath, robots: 'noindex, follow' };
  }

  return {
    canonicalPath: page > 1 ? `${baseCanonicalPath}?page=${page}` : baseCanonicalPath,
    robots: indexableRobots,
  };
}

export function getCatalogSeoText(seoData?: Partial<Pick<SeoData, 'text'>> | null): string | null {
  const text = seoData?.text?.trim();

  return text || null;
}

export function getCatalogSeoDescription(
  seoData?: Partial<Pick<SeoData, 'description' | 'text'>> | null,
): string | null {
  if (getCatalogSeoText(seoData)) return null;

  const description = seoData?.description?.trim();

  return description || null;
}

export function getOfferDisplayName(offer: OfferWithSeoHeader): string {
  return offer.product_name_with_offer_properties || offer.product_name;
}

export function getProductSchemaWithSeoHeader(schema: Schema, header?: string): Schema {
  return header ? { ...schema, name: header } : schema;
}

export function getItemListSchemaWithSeoHeaders(schema: ItemListSchema, offers: OfferWithSeoHeader[]): ItemListSchema {
  return {
    ...schema,
    itemListElement: schema.itemListElement.map((item, index) => {
      const header = offers[index]?.seo_data?.header;

      return header ? { ...item, name: header } : item;
    }),
  };
}
